import { type HTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface ProgressBarProps extends HTMLAttributes<HTMLDivElement> {
  /** Fill width in pixels or any CSS length value. */
  width: number | string;
  /** Background color of the filled portion. */
  color: string;
  /** Height of the bar in pixels (default: 6). */
  height?: number;
}

/**
 * Thin colored bar used to visualise vote/prediction distribution.
 *
 * Renders a fixed-height track with a colored fill whose width
 * represents a proportion (e.g. option popularity). The fill is shaped
 * like a pennant — straight on the left, tapering into a triangular
 * tip on the right — and carries a subtle inset highlight + shadow
 * (`shadow-bar`) for a glossy, embossed look matching the Figma
 * design.
 *
 * @example
 * ```tsx
 * <ProgressBar width={100} color="#812BDF" />         // purple, 100px
 * <ProgressBar width={73}  color="#317CFD" height={3} /> // blue, 73px, 3px tall
 * ```
 */
const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  ({ className, width, color, height = 6, ...props }, ref) => {
    const tip = Math.max(2, Math.round(height / 2));
    return (
      <div ref={ref} className={cn('w-full', className)} {...props}>
        <div
          className="shadow-bar"
          style={{
            width: typeof width === 'number' ? `${width}px` : width,
            height: `${height}px`,
            backgroundColor: color,
            clipPath: `polygon(0 0, calc(100% - ${tip}px) 0, 100% 50%, calc(100% - ${tip}px) 100%, 0 100%)`,
          }}
        />
      </div>
    );
  },
);
ProgressBar.displayName = 'ProgressBar';

export { ProgressBar };
